import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '@material-ui/core/Grid';
import { createStructuredSelector } from 'reselect';
import { get } from 'lodash';
import { compose } from 'redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DialogWithTitle from '../DialogWithTitle';
import { useTranslation, withTranslation } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import ArtistImages from '../ArtistViewsComponents/ImagesView';
import ArtistVideos from '../ArtistViewsComponents/VideosView';
import FbCircularProgress from '../FbCircularProgress';
import { TP } from '../../constants/consts';
import ProductionAccordion from '../ProductionAccordion';

import {
  selectChosenProfileData,
  selectChosenRepetorieProductions,
  selectRepetorieImages,
  selectRepetorieVideos,
  selectRepetorieModalStatus,
  selectChosenRepetorie,
} from '../../containers/Artist/selectors';

import { selectIsLoggedInState } from '../../store/selectors';

import FbTabsSection from '../FbTabsSection';
import TabPanel from '../TabPanel';

const useStyles = makeStyles(theme => ({
  titleHeader: {
    color: colors.grey3,
    fontWeight: 'bold',
    '& h6': {
      margin: 0,
      fontSize: fontSize.h6,
    },
    '& p': {
      margin: 0,
      fontSize: fontSize.medium,
    },
    '& span': {
      fontWeight: 'normal',
      fontStyle: 'italic',
    },
  },
  tabsView: {
    borderBottom: 'solid 1px rgb(58, 58, 58, 0.2)',
    '& .MuiTab-wrapper': {
      color: '#555555',
    },
  },
  appBarClass: {
    padding: `0 ${theme.spacing(2)}`,
    borderBottom: 0,
  },
  displaySection: {
    padding: theme.spacing(2),
  },
  cardsSection: {
    marginTop: 16,
  },
}));

const RepertorieDialog = ({
  chosenRepetorie,
  profile,
  artistProductions,
  isLoggedIn,
  isOpen,
  handleModal,
  images,
  videos,
  pageView = 'production',
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { name, profession } = profile;
  const work = get(chosenRepetorie, 'work', null);
  const workRole = get(chosenRepetorie, 'workRole', null);

  const getTitleComponent = () => (
    <div className={classes.titleHeader}>
      <h6>
        {name} <span>({profession?.qualification?.name})</span>
      </h6>
      <p>
        {work?.name}
        <span>, {workRole?.name}</span>{' '}
      </p>
    </div>
  );

  const tabs = [
    {
      name: t(`${TP}.m_PERF`),
      value: 'productions',
      isShown: artistProductions?.data?.length > 0,
      mainContent: artistProductions?.data?.length > 0 && (
        <ProductionAccordion
          viewType={pageView}
          productions={artistProductions?.data || []}
          isLoggedIn={isLoggedIn}
          className={classes.cardsSection}
          profile={profile}
        />
      ),
      count: artistProductions ? artistProductions?.data?.length : 0,
    },
    {
      name: t(`${TP}.m_ARTIST_VIDEOS`),
      value: 'videos',
      isShown: videos?.data?.length > 0,
      mainContent: <ArtistVideos t={t} videos={videos} />,
      count: videos?.data?.length,
    },
    {
      name: t(`${TP}.m_IMAGES`),
      value: 'images',
      isShown: images?.data?.length > 0,
      mainContent: <ArtistImages t={t} photos={images} />,
      count: images?.data?.length,
    },
  ];

  const [selectedTab, onTabSelect] = React.useState(tabs[0].value);

  const onChange = value => {
    onTabSelect(value);
  };

  return (
    <DialogWithTitle
      open={isOpen}
      onClose={() => handleModal(false)}
      titleComponent={getTitleComponent()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
    >
      {artistProductions?.loading ? (
        <FbCircularProgress />
      ) : (
        <>
          <div className={classes.tabsView}>
            {tabs && artistProductions?.data?.length > 0 && (
              <FbTabsSection
                appBarClass={classes.appBarClass}
                tabs={tabs.filter(item => item.isShown)}
                onChange={onChange}
                initialTabValue={selectedTab}
                scrollable
              />
            )}
          </div>
          <div className={classes.displaySection}>
            {tabs.map((item, index) => (
              <TabPanel
                key={`popUpTab-${index}`}
                value={selectedTab}
                currentVal={item.value}
                className={classes.tabPanel}
              >
                <Grid item xs={12} md={12} sm={12}>
                  {item.mainContent && <div>{item.mainContent}</div>}
                </Grid>
              </TabPanel>
            ))}
          </div>
        </>
      )}
    </DialogWithTitle>
  );
};

const mapStateToProps = createStructuredSelector({
  profile: selectChosenProfileData(),
  artistProductions: selectChosenRepetorieProductions(),
  images: selectRepetorieImages(),
  videos: selectRepetorieVideos(),
  isLoggedIn: selectIsLoggedInState(),
  isOpen: selectRepetorieModalStatus(),
  chosenRepetorie: selectChosenRepetorie(),
});
const withConnect = connect(mapStateToProps, null);

RepertorieDialog.propTypes = {
  profile: PropTypes.object,
  artistProductions: PropTypes.object,
  isOpen: PropTypes.bool,
  images: PropTypes.object,
  videos: PropTypes.object,
  getRepetorieProductions: PropTypes.func,
};

export default compose(withConnect, withTranslation())(RepertorieDialog);
